<script setup lang="ts">
import type { Component } from 'vue'

import { Grid, List } from '@/shared/icons'
import type { ListView } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { RadioGroup, RadioButton } from '@/shared/ui/RadioGroup'

/**
 * Cards or table, for a list that can be drawn either way. Sits in a `TableToolbar`'s
 * `#right` slot; pair it with `useViewMode` to remember the choice.
 *
 * The two options are fixed on purpose — the point of a shared switch is that the
 * choice reads the same on every screen that offers it.
 */
const VIEWS: ReadonlyArray<{
  value: ListView
  label: string
  icon: Component
}> = [
  { value: 'cards', label: 'Cards', icon: Grid },
  { value: 'table', label: 'Table', icon: List }
]

withDefaults(
  defineProps<{
    modelValue: ListView
    size?: 'small' | 'default' | 'large'
  }>(),
  { size: 'small' }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: ListView): void
}>()
</script>

<template>
  <RadioGroup
    :model-value="modelValue"
    :size="size"
    @update:model-value="emit('update:modelValue', $event as ListView)"
  >
    <RadioButton
      v-for="view in VIEWS"
      :key="view.value"
      :value="view.value"
      :title="view.label"
    >
      <Icon>
        <component :is="view.icon" />
      </Icon>
    </RadioButton>
  </RadioGroup>
</template>
